import dayjs from "dayjs";
import { useContext } from "react";
import { MediaTask } from "@coworker/types/lib/tasks/media";
import { TaskCard } from "./TaskCard";
import { FullWidthButton } from "../../SkapaButton";
import { useTranslation } from "@coworker/locales";
import {
  getPassedTimeAndTranslationKey,
  getPiecesTranslation,
  isOngoing,
} from "../tools";
import { Column, ListItemWrapper } from "../styles/styles";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { ModalContext } from "@coworker/apprestructured/src/layout/context/ModalContextProvider";
import { ModalType } from "@coworker/apprestructured/src/layout/context/modalType";
import { Text } from "../Atoms/Text";
import { LocationPin } from "../Atoms/Icons/LocationPin";
import { formatFullIdWithDots } from "@coworker/reusable/formatters/product.formatter";
import { FixaProductIdentifier } from "@coworker/apprestructured/src/shared/wrappers/FixaProductIdentifier/FixaProductIdentifier";
import styled from "styled-components";

const SubTextRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

interface CreateTaskCardProps {
  task: MediaTask | undefined;
  mediaName: string;
  pieces: number;
  isCurrentArticle: boolean;
  articleId: string;
  mediaId: string;
  groupName: string;
  groupId: string;
  localCombinationParentArticle?: string;
}

export function CreateTaskCard({
  task,
  mediaName,
  pieces,
  isCurrentArticle,
  articleId,
  mediaId,
  groupName,
  groupId,
  localCombinationParentArticle,
}: CreateTaskCardProps) {
  const { t } = useTranslation();
  const navigate = useFixaNavigate();
  const { toggleModal } = useContext(ModalContext);

  const headLine = `${mediaName}: ${pieces} ${t(getPiecesTranslation(pieces))}${
    isCurrentArticle ? ` ${t("selectedString")}` : ""
  }${groupName ? ` ${t("inGroupString")}` : ""}`;
  const isLocalCombinationSubArticle = !!localCombinationParentArticle;

  const subTextDesc =
    isLocalCombinationSubArticle && groupName
      ? `${groupName} - ${t("partOfLocalCombinationString")}`
      : isLocalCombinationSubArticle
        ? t("partOfLocalCombinationString")
        : `${groupName}`;

  let subText = subTextDesc ? (
    <SubTextRow>
      <Text text={subTextDesc} />
      {isLocalCombinationSubArticle && (
        <FixaProductIdentifier
          value={formatFullIdWithDots(localCombinationParentArticle)}
        />
      )}
    </SubTextRow>
  ) : (
    <></>
  );

  let buttonText = t("createString");

  let isOngoingTask = false;
  if (task) {
    const createdAt = new Date(task.created_at).toISOString();
    const timePassedAndKey = getPassedTimeAndTranslationKey(
      dayjs().toISOString(),
      createdAt
    );
    isOngoingTask = isOngoing(task);

    let timePassed = `${timePassedAndKey.passedTime} ${t(
      timePassedAndKey.translationKey
    )}`;
    if (timePassedAndKey.translationKey === "lessThanDayString") {
      timePassed = t(timePassedAndKey.translationKey);
    }

    subText = (
      <Column>
        {subText}
        <Text
          text={`${t("lastCreatedString")}: ${
            isOngoingTask ? t("ongoingString") : timePassed
          }`}
        />
      </Column>
    );
    buttonText = isOngoingTask ? t("showTaskString") : t("createString");
  }

  const onClick = () => {
    if (isOngoingTask) {
      toggleModal({
        type: ModalType.TaskDetail,
        payload: { taskId: task?.id, taskType: task?.task_type },
      });
    } else {
      navigate(`/mediamaintenance/${mediaId}/articles/${articleId}/createtask`);
    }
  };

  const mediaChevronOnClick = () => {
    if (groupId) {
      navigate(`/mediamaintenance/groups/${groupId}?mediaId=${mediaId}`);
    } else {
      navigate(`/mediamaintenance/tabs/${mediaId}?tab=article-list-tab`);
    }
  };

  return (
    <>
      <TaskCard
        leftIcon={<LocationPin />}
        headLine={headLine}
        subText={subText}
        control="navigational"
        rightIcon="chevronRightSmall"
        onClick={mediaChevronOnClick}
      />
      {!isLocalCombinationSubArticle && (
        <ListItemWrapper $hasBanner={false}>
          <FullWidthButton
            text={buttonText}
            onClick={onClick}
            type={isOngoingTask ? "secondary" : "primary"}
          />
        </ListItemWrapper>
      )}
    </>
  );
}
